import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import {ScrollContext} from "../../context/ScrollContext";

const SmoothScrollWrapper = forwardRef(({children}, ref) => {
  const scrollRef = useRef(null);
  const offset = useRef(0);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  let raf;

  // ✅ Detect device type
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // ✅ Expose scrollToId for header
  useImperativeHandle(ref, () => ({
    scrollToId: (id) => {
      const target = document.getElementById(id);
      if (target) {
        const rect = target.getBoundingClientRect();
        const scrollTarget = offset.current + rect.top;
        window.scrollTo({top: scrollTarget, behavior: "smooth"});
      }
    },
  }));

  // ✅ Core scroll effect
  useEffect(() => {
    const scrollEl = scrollRef.current;
    const body = document.body;

    const updateBodyHeight = () => {
      if (scrollEl && !isMobile) {
        const height = scrollEl.getBoundingClientRect().height;
        body.style.height = `${Math.floor(height)}px`;
      } else {
        body.style.height = "auto";
      }
    };

    const resizeObserver = new ResizeObserver(updateBodyHeight);
    if (scrollEl) resizeObserver.observe(scrollEl);

    if (!isMobile) {
      const smoothScroll = () => {
        offset.current += (window.pageYOffset - offset.current) * 0.1;
        scrollEl.style.transform = `translateY(-${offset.current}px) translateZ(0)`;
        setScrollY(offset.current);
        raf = requestAnimationFrame(smoothScroll);
      };

      updateBodyHeight();
      smoothScroll();
      window.addEventListener("resize", updateBodyHeight);

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", updateBodyHeight);
        resizeObserver.disconnect();
      };
    } else {
      // Mobile fallback: disable transform
      if (scrollEl) scrollEl.style.transform = "none";
      updateBodyHeight();
      setScrollY(window.scrollY); // initial set
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMobile]);

  return (
    <ScrollContext.Provider value={scrollY}>
      <div className="js-cont">
        <div className="js-scroll" ref={scrollRef}>
          {children}
        </div>
      </div>
    </ScrollContext.Provider>
  );
});

export default SmoothScrollWrapper;
